import { useState, type ReactElement } from "react";

import { CsvDownloadButton, CsvDownloadError, useCsvDownload } from "../../components/csv-download-button.js";

import { adminCenterApi } from "./urls.js";

/** 請求の CSV で選べる月（サーバの CenterInvoiceMonthChoice）。 */
export interface CenterInvoiceMonth {
    /** 口の <c>id</c> に渡す年月（yyyyMM）。 */
    readonly value: string;
    /** 画面に出す名前（yyyy年MM月）。 */
    readonly label: string;
}

/**
 * メニューの「請求の CSV」の欄（サーバの CenterInvoiceCsvViewModel）。<b>請求の権限を持つ人にだけ返る</b>。
 * 選べる月と既定の月（前の月）はサーバが決める（Implem.Portal.Backend の docs/features/32 §2）。
 */
export interface CenterInvoiceCsv {
    /** 選べる月（新しい月が先頭）。 */
    readonly months: readonly CenterInvoiceMonth[];
    /** 最初に選んでおく月。 */
    readonly defaultMonth: string;
}

/**
 * 開く前に聞く文。<b>CSV を作ると、期間が終わった月の請求書がその時点の内容で固定される</b>（作り直すまで変わらない）。
 * 全契約が対象なので、取り消しにくい操作として聞く（docs/01 §7）。
 */
export const invoiceCsvDialog = (monthLabel: string): string =>
    `「${monthLabel}」の全契約の請求を CSV でダウンロードします。`
    + "全契約の請求書のうち、期間が終わった月はこの時点の内容で固定されます。よろしいですか？";

/**
 * 年月を選んで請求の CSV を落とす（docs/screens/33）。
 *
 * <b>ボタンは fetch で受け取って保存させる</b>（<c>components/csv-download-button.tsx</c>。<c>csv-download.tsx</c> と同じ部品）。
 * 押したときに確認のダイアログで聞き、断られたら受け取らない。失敗したら欄の下に断りを出す（画面は移らない）。
 * 契約の番号は口が使わないので 0 を載せる。
 */
export function InvoiceCsvDownload({ csv }: { readonly csv: CenterInvoiceCsv }): ReactElement {
    const [month, setMonth] = useState(csv.defaultMonth);
    const label = csv.months.find((o) => o.value === month)?.label ?? month;

    const download = useCsvDownload({
        url: adminCenterApi.invoiceCsv(month),
        filePrefix: `invoices_${month}`,
        dialog: () => invoiceCsvDialog(label),
    });

    return (
        <div data-invoice-csv="">
            <div className="flex flex-wrap items-end gap-4">
                <div>
                    <label htmlFor="invoiceCsvMonth" className="form-label">請求の月</label>
                    <select
                        id="invoiceCsvMonth"
                        className="form-input"
                        value={month}
                        disabled={download.busy}
                        onChange={(event) => { setMonth(event.currentTarget.value); }}
                    >
                        {csv.months.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                </div>
                <CsvDownloadButton download={download} className="btn-secondary" />
            </div>
            <CsvDownloadError download={download} />
        </div>
    );
}
